/**
 * WA0184+WA0186 · Página pública de firma manuscrita.
 * URL: /firma/:token (sin auth)
 * Muestra el documento a firmar + canvas HTML5 para dibujar la firma.
 */
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast, Toaster } from "sonner";
import axios from "axios";
import { CheckCircle2, RotateCcw, Loader2, ShieldCheck, FileText } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function FirmaPublicaPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [firmando, setFirmando] = useState(false);
  const [nombre, setNombre] = useState("");
  const [dni, setDni] = useState("");
  const [acepta, setAcepta] = useState(false);
  const [firmado, setFirmado] = useState(false);
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const empty = useRef(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${BACKEND}/api/firmas-publica/${token}`);
        setData(r.data);
        if (r.data.estado === "firmado") setFirmado(true);
        if (r.data.destinatario_nombre) setNombre(r.data.destinatario_nombre);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Enlace inválido o caducado");
      } finally { setLoading(false); }
    })();
  }, [token]);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.strokeStyle = "#0F1A3C"; ctx.lineWidth = 2.2; ctx.lineCap = "round"; ctx.lineJoin = "round";
    const pos = (e) => {
      const r = c.getBoundingClientRect();
      const t = e.touches?.[0];
      return { x: (t ? t.clientX : e.clientX) - r.left, y: (t ? t.clientY : e.clientY) - r.top };
    };
    const start = (e) => { drawing.current = true; empty.current = false; const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); e.preventDefault(); };
    const move = (e) => { if (!drawing.current) return; const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); e.preventDefault(); };
    const end = () => { drawing.current = false; };
    c.addEventListener("mousedown", start); c.addEventListener("mousemove", move);
    c.addEventListener("mouseup", end); c.addEventListener("mouseleave", end);
    c.addEventListener("touchstart", start); c.addEventListener("touchmove", move);
    c.addEventListener("touchend", end);
    return () => {
      c.removeEventListener("mousedown", start); c.removeEventListener("mousemove", move);
      c.removeEventListener("mouseup", end); c.removeEventListener("mouseleave", end);
      c.removeEventListener("touchstart", start); c.removeEventListener("touchmove", move);
      c.removeEventListener("touchend", end);
    };
  }, [firmando, firmado, data]);

  const limpiar = () => {
    const c = canvasRef.current; if (!c) return;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    empty.current = true;
  };

  const confirmar = async () => {
    if (!nombre.trim()) { toast.error("Escribe tu nombre completo"); return; }
    if (!acepta) { toast.error("Debes aceptar el contenido para firmar"); return; }
    if (empty.current) { toast.error("Dibuja tu firma antes de confirmar"); return; }
    const dataUrl = canvasRef.current.toDataURL("image/png");
    try {
      setFirmando(true);
      await axios.post(`${BACKEND}/api/firmas-publica/${token}/firmar`, {
        firma_dataurl: dataUrl, nombre, dni: dni || null,
      });
      setFirmado(true);
      toast.success("Firma registrada correctamente");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo registrar la firma");
    } finally { setFirmando(false); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200">
      <Loader2 className="h-6 w-6 animate-spin text-[#1E5BCC]" />
    </div>
  );
  if (!data) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100">
      <div className="max-w-md text-center p-8 bg-white rounded-xl shadow-lg">
        <FileText className="h-12 w-12 mx-auto text-red-500 mb-3" />
        <h1 className="text-lg font-extrabold">Enlace no válido</h1>
        <p className="text-sm text-slate-600 mt-2">Solicita al remitente un nuevo enlace de firma.</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200 py-8 px-4">
      <Toaster position="top-center" richColors />
      <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden" data-testid="firma-publica-page">
        {/* Header */}
        <div className="p-6 text-white" style={{ background: "linear-gradient(135deg,#0F1A3C,#1E5BCC)" }}>
          <div className="flex items-center gap-3">
            <ShieldCheck className="h-8 w-8" />
            <div>
              <div className="text-[10px] uppercase tracking-widest font-extrabold opacity-70">LEGATUM CAPITAL · Firma Digital</div>
              <h1 className="text-xl font-extrabold" data-testid="firma-titulo">{data.documento_titulo}</h1>
              <div className="text-xs opacity-80 mt-1">Destinatario: {data.destinatario_nombre}</div>
            </div>
          </div>
        </div>

        {/* Documento */}
        <div className="p-6 border-b bg-white">
          <div className="text-[10px] uppercase tracking-widest font-extrabold text-slate-500 mb-2">Documento a firmar</div>
          <div className="max-h-[45vh] overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 p-4"
               data-testid="firma-documento"
               dangerouslySetInnerHTML={{ __html: data.documento_html || "(sin contenido)" }} />
        </div>

        {/* Firma o confirmación */}
        {firmado ? (
          <div className="p-8 text-center" data-testid="firma-confirmada">
            <CheckCircle2 className="h-14 w-14 mx-auto text-emerald-500 mb-3" />
            <h2 className="text-lg font-extrabold text-emerald-700">Firma registrada</h2>
            <p className="text-sm text-slate-600 mt-2">
              Gracias, {nombre}. Recibirás una copia del documento firmado por email.
            </p>
          </div>
        ) : (
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest font-extrabold text-slate-500">Nombre y apellidos *</label>
                <input value={nombre} onChange={(e) => setNombre(e.target.value)}
                       data-testid="firma-nombre-input"
                       className="w-full mt-1 px-3 py-2 rounded border border-slate-300 text-sm" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest font-extrabold text-slate-500">DNI/NIE (opcional)</label>
                <input value={dni} onChange={(e) => setDni(e.target.value)}
                       data-testid="firma-dni-input"
                       className="w-full mt-1 px-3 py-2 rounded border border-slate-300 text-sm" />
              </div>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-widest font-extrabold text-slate-500 flex items-center justify-between">
                <span>Firma manuscrita *</span>
                <button onClick={limpiar} className="text-[10px] font-bold text-slate-500 inline-flex items-center gap-1 hover:text-slate-700"
                        data-testid="firma-limpiar">
                  <RotateCcw className="h-3 w-3" /> Limpiar
                </button>
              </label>
              <canvas ref={canvasRef} width={700} height={220}
                      data-testid="firma-canvas"
                      className="w-full mt-1 border-2 border-dashed border-slate-300 rounded-lg bg-white cursor-crosshair touch-none" />
              <p className="text-[10px] text-slate-500 mt-1 italic">
                Dibuja tu firma con el ratón o con el dedo (dispositivos táctiles).
              </p>
            </div>

            <label className="flex items-start gap-2 text-xs text-slate-700">
              <input type="checkbox" checked={acepta} onChange={(e) => setAcepta(e.target.checked)}
                     data-testid="firma-acepta"
                     className="mt-0.5" />
              <span>
                He leído el documento anterior y lo acepto en su integridad. Declaro que la
                firma dibujada es la mía y que actúo en nombre propio o con poder de
                representación suficiente.
              </span>
            </label>

            <button onClick={confirmar} disabled={firmando}
                    data-testid="firma-confirmar"
                    className="w-full py-3 rounded-lg font-extrabold text-white shadow-md hover:shadow-lg transition-shadow disabled:opacity-50"
                    style={{ background: "linear-gradient(135deg,#0F8A5F,#0A6644)" }}>
              {firmando ? <><Loader2 className="h-4 w-4 mr-2 inline animate-spin" /> Registrando…</>
                        : <><CheckCircle2 className="h-4 w-4 mr-2 inline" /> Confirmar y firmar</>}
            </button>
          </div>
        )}

        <div className="text-center py-3 text-[10px] text-slate-500 border-t border-slate-100">
          Firma manuscrita electrónica con registro de IP y sello temporal. Powered by LEGATUM CAPITAL.
        </div>
      </div>
    </div>
  );
}
